import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { 
  ShieldAlert, 
  Users, 
  LogOut, 
  Globe, 
  Search, 
  RefreshCw, 
  Ban, 
  CheckCircle, 
  AlertTriangle,
  Lock,
  Clock,
  UserX
} from 'lucide-react';

interface SuspiciousUser {
  user_id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  role: string;
  conflicts_count: number;
  last_conflict_at: string;
  ips: string[];
  cities_count?: number;
  cities_list?: string[];
  risk_score?: number;
  risk_level?: 'CRITICAL' | 'HIGH' | 'MODERATE';
  detected_anomalies?: string[];
  details_sample?: { created_at: string; ip: string; details: string }[];
}

interface SummaryData {
  days: number;
  total_conflicts: number;
  total_logins: number;
  unique_ips: number;
  suspicious_users_count: number;
  suspicious_users: SuspiciousUser[];
}

interface AuditLogItem {
  id: number;
  user_id: number;
  action: string;
  details: string;
  ip_address: string;
  ip_location?: string;
  created_at: string;
  user?: {
    id: number;
    email: string;
    full_name: string;
    is_active: boolean;
  };
}

const AdminSessionAnomaliesScreen: React.FC = () => {
  const { token } = useAuth();
  const [days, setDays] = useState<number>(30);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionFilter, setActionFilter] = useState<string>('SESSION_CONFLICT');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/v1/admin/anomalies/summary?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [token, days]);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    try {
      let url = `${API_URL}/v1/admin/anomalies/logs?days=${days}&limit=300`;
      if (actionFilter && actionFilter !== 'ALL') {
        url += `&action=${actionFilter}`;
      }
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  }, [token, days, actionFilter, searchQuery]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSummary(), fetchLogs()]);
    setLoading(false);
  }, [fetchSummary, fetchLogs]);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, loadData]);

  const handleRevokeSessions = async (userId: number, email: string) => {
    if (!window.confirm(`Voulez-vous vraiment déconnecter TOUTES les sessions actives de ${email} ?`)) {
      return;
    }
    setActionLoading(userId);
    try {
      const res = await fetch(`${API_URL}/v1/admin/users/${userId}/revoke-sessions`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMessage({ text: `Sessions révoquées avec succès pour ${email}.`, type: 'success' });
        loadData();
      } else {
        setMessage({ text: 'Erreur lors de la révocation des sessions.', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Erreur réseau.', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (userId: number, email: string, currentStatus: boolean) => {
    const actionText = currentStatus ? 'SUSPENDRE' : 'RÉACTIVER';
    if (!window.confirm(`Voulez-vous vraiment ${actionText} le compte de ${email} ?`)) {
      return;
    }
    setActionLoading(userId);
    try {
      const res = await fetch(`${API_URL}/v1/admin/users/${userId}/toggle-status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMessage({ text: `Statut de ${email} mis à jour avec succès.`, type: 'success' });
        loadData();
      } else {
        setMessage({ text: 'Erreur lors du changement de statut.', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Erreur réseau.', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (isoStr: string) => {
    if (!isoStr) return '—';
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch {
      return isoStr;
    }
  };

  return (
    <SiteLayout>
      <Header icon="shield" title="🛡️ Sécurité & Anomalies de Session" />

      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Banner Alert Toast */}
        {message && (
          <div style={{
            padding: '12px 20px', borderRadius: '10px', marginBottom: '20px',
            background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: message.type === 'success' ? '#166534' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
          </div>
        )}

        {/* Top Controls Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ShieldAlert style={{ color: '#ef4444' }} size={26} />
              Détection des Partages de Compte
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
              Identification automatique des sessions en conflit, expulsions en temps réel et suivi des adresses IP.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', padding: '6px 14px', borderRadius: '10px' }}>
              <Clock size={16} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>Période :</span>
              <select
                value={days}
                onChange={e => setDays(Number(e.target.value))}
                style={{ border: 'none', background: 'transparent', fontSize: '13px', fontWeight: 700, color: '#2563eb', cursor: 'pointer', outline: 'none' }}
              >
                <option value={7}>Derniers 7 jours</option>
                <option value={14}>Derniers 14 jours</option>
                <option value={30}>Derniers 30 jours (1 mois)</option>
                <option value={90}>Derniers 90 jours (3 mois)</option>
              </select>
            </div>

            <button
              onClick={loadData}
              disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '10px',
                border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-heading)',
                fontWeight: 600, cursor: 'pointer'
              }}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualiser
            </button>
          </div>
        </div>

        {/* Stats KPI Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <Box style={{ padding: '20px', borderLeft: '4px solid #ef4444' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#ef4444', letterSpacing: '0.05em' }}>Conflits de Session</span>
              <ShieldAlert size={22} style={{ color: '#ef4444' }} />
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, margin: '8px 0 2px 0', color: 'var(--text-heading)' }}>
              {summary ? summary.total_conflicts : '—'}
            </div>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Sessions précédentes expulsées</span>
          </Box>

          <Box style={{ padding: '20px', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#f59e0b', letterSpacing: '0.05em' }}>Comptes Suspects</span>
              <UserX size={22} style={{ color: '#f59e0b' }} />
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, margin: '8px 0 2px 0', color: 'var(--text-heading)' }}>
              {summary ? summary.suspicious_users_count : '—'}
            </div>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Utilisateurs avec doubles connexions</span>
          </Box>

          <Box style={{ padding: '20px', borderLeft: '4px solid #2563eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#2563eb', letterSpacing: '0.05em' }}>Adresses IP Uniques</span>
              <Globe size={22} style={{ color: '#2563eb' }} />
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, margin: '8px 0 2px 0', color: 'var(--text-heading)' }}>
              {summary ? summary.unique_ips : '—'}
            </div>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Adresses IP répertoriées ({days}j)</span>
          </Box>

          <Box style={{ padding: '20px', borderLeft: '4px solid #10b981' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#10b981', letterSpacing: '0.05em' }}>Connexions Totales</span>
              <Users size={22} style={{ color: '#10b981' }} />
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, margin: '8px 0 2px 0', color: 'var(--text-heading)' }}>
              {summary ? summary.total_logins : '—'}
            </div>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Logins réussis dans la période</span>
          </Box>
        </div>

        {/* Section 0: Encadré Spécial - Suspects Avérés (Règles Sécurité) */}
        {summary && summary.suspicious_users.some(u => (u.risk_score || 0) >= 40 || (u.detected_anomalies && u.detected_anomalies.length > 0)) && (
          <Box style={{
            padding: '24px', marginBottom: '32px', borderLeft: '6px solid #dc2626',
            background: 'linear-gradient(135deg, rgba(254, 242, 242, 0.6) 0%, rgba(255, 255, 255, 0.9) 100%)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center' }}>
                <ShieldAlert size={26} />
              </div>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#991b1b' }}>
                  🎯 Encadré d'Alerte : Partage de Compte Suspecté (Détection par Règles Sécurité)
                </h2>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#7f1d1d' }}>
                  Comptes présentant des signaux de partage avéré : voyages impossibles, bascules rapides de villes ou expulsions de session consécutives.
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '16px' }}>
              {summary.suspicious_users.filter(u => (u.risk_score || 0) >= 40 || (u.detected_anomalies && u.detected_anomalies.length > 0)).map(u => (
                <div key={`alert-${u.user_id}`} style={{
                  padding: '16px', borderRadius: '12px', background: '#ffffff',
                  border: `2px solid ${(u.risk_score || 0) >= 70 ? '#fca5a5' : '#fde68a'}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  {/* Top Bar */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <strong style={{ fontSize: '15px', color: 'var(--text-heading)' }}>{u.full_name || 'Sans Nom'}</strong>
                      <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>{u.email}</div>
                    </div>
                    <span style={{
                      padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800,
                      background: (u.risk_score || 0) >= 70 ? '#fee2e2' : '#fef3c7',
                      color: (u.risk_score || 0) >= 70 ? '#991b1b' : '#92400e',
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}>
                      🚨 RISQUE {(u.risk_score || 0) >= 70 ? 'CRITIQUE' : 'ÉLEVÉ'} ({u.risk_score || 0}%)
                    </span>
                  </div>

                  {/* Detected Anomalies / Rules */}
                  <div style={{
                    background: 'var(--bg-secondary)', padding: '10px 12px', borderRadius: '8px',
                    fontSize: '12px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px'
                  }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase' }}>
                      Règles de détection déclenchées :
                    </div>
                    {u.detected_anomalies && u.detected_anomalies.length > 0 ? (
                      u.detected_anomalies.map((anom, idx) => (
                        <div key={idx} style={{ color: '#b91c1c', fontWeight: 600, lineHeight: 1.4 }}>
                          {anom}
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#b91c1c', fontWeight: 600 }}>
                        ⚡ {u.conflicts_count} conflits de session répétés (double utilisation simultanée)
                      </div>
                    )}
                  </div>

                  {/* Summary IP list */}
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    <strong>IPs & Villes :</strong> {u.ips.join(' • ')}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      disabled={actionLoading === u.user_id}
                      onClick={() => handleRevokeSessions(u.user_id, u.email)}
                      style={{
                        padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                        border: '1px solid #f59e0b', background: '#fffbeb', color: '#b45309', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px'
                      }}
                    >
                      <LogOut size={13} /> Éjecter Sessions
                    </button>
                    <button
                      disabled={actionLoading === u.user_id}
                      onClick={() => handleToggleStatus(u.user_id, u.email, u.is_active)}
                      style={{
                        padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                        border: u.is_active ? '1px solid #ef4444' : '1px solid #10b981',
                        background: u.is_active ? '#fef2f2' : '#ecfdf5',
                        color: u.is_active ? '#dc2626' : '#059669', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px'
                      }}
                    >
                      {u.is_active ? <Ban size={13} /> : <CheckCircle size={13} />}
                      {u.is_active ? 'Suspendre Compte' : 'Réactiver'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Box>
        )}

        {/* Section 1: Aggregated Suspicious Accounts Table */}
        <Box style={{ padding: '24px', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                Tableau Général des Utilisateurs Suspects ({summary?.suspicious_users.length || 0})
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                Classement par niveau de risque et nombre de conflits enregistrés.
              </p>
            </div>
          </div>

          {!summary || summary.suspicious_users.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <CheckCircle size={32} style={{ color: '#10b981', marginBottom: '8px' }} />
              <div>Aucune anomalie de session majeure détectée sur les {days} derniers jours.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '12px' }}>Score Risque</th>
                    <th style={{ padding: '12px' }}>Utilisateur</th>
                    <th style={{ padding: '12px' }}>Rôle</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Conflits</th>
                    <th style={{ padding: '12px' }}>IPs Client & Localisations</th>
                    <th style={{ padding: '12px' }}>Anomalies Détectées</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Statut</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Actions Sécurité</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.suspicious_users.map(u => (
                    <tr key={u.user_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '14px 12px' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800,
                          background: (u.risk_score || 0) >= 70 ? '#fee2e2' : ((u.risk_score || 0) >= 40 ? '#fef3c7' : '#e0f2fe'),
                          color: (u.risk_score || 0) >= 70 ? '#991b1b' : ((u.risk_score || 0) >= 40 ? '#92400e' : '#075985')
                        }}>
                          {u.risk_score || 0}%
                        </span>
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-heading)' }}>{u.full_name || 'Sans Nom'}</div>
                        <div style={{ fontSize: '12px', color: '#2563eb' }}>{u.email}</div>
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                          {u.role}
                        </span>
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 800,
                          background: u.conflicts_count > 3 ? '#fee2e2' : '#fef3c7',
                          color: u.conflicts_count > 3 ? '#991b1b' : '#92400e'
                        }}>
                          🚨 {u.conflicts_count}
                        </span>
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', maxWidth: '300px' }}>
                          {u.ips.map(ip => (
                            <span key={ip} style={{ fontSize: '11px', fontFamily: 'monospace', padding: '2px 6px', borderRadius: '4px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                              {ip}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '14px 12px', maxWidth: '280px', fontSize: '12px' }}>
                        {u.detected_anomalies && u.detected_anomalies.length > 0 ? (
                          <div style={{ color: '#b91c1c', fontWeight: 600, fontSize: '11px', lineHeight: 1.4 }}>
                            {u.detected_anomalies[0]}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                        {formatDate(u.last_conflict_at)}
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                        {u.is_active ? (
                          <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <CheckCircle size={14} /> Actif
                          </span>
                        ) : (
                          <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Ban size={14} /> Suspendu
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button
                            disabled={actionLoading === u.user_id}
                            onClick={() => handleRevokeSessions(u.user_id, u.email)}
                            title="Invalide la session et force la déconnexion immédiate sur tous les appareils"
                            style={{
                              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                              border: '1px solid #f59e0b', background: '#fffbeb', color: '#b45309', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                          >
                            <LogOut size={13} /> Éjecter Sessions
                          </button>

                          <button
                            disabled={actionLoading === u.user_id}
                            onClick={() => handleToggleStatus(u.user_id, u.email, u.is_active)}
                            style={{
                              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                              border: u.is_active ? '1px solid #ef4444' : '1px solid #10b981',
                              background: u.is_active ? '#fef2f2' : '#ecfdf5',
                              color: u.is_active ? '#dc2626' : '#059669', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                          >
                            {u.is_active ? <Ban size={13} /> : <CheckCircle size={13} />}
                            {u.is_active ? 'Suspendre' : 'Réactiver'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Box>

        {/* Section 2: Detailed Audit Log Stream */}
        <Box style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--text-heading)' }}>
                📋 Journal des Événements & Conflits de Session
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                Flux chronologique des connexions, tentatives et expulsions enregistrées.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {/* Action Filter */}
              <select
                value={actionFilter}
                onChange={e => setActionFilter(e.target.value)}
                style={{
                  padding: '8px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                  border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-heading)'
                }}
              >
                <option value="ALL">Toutes les actions</option>
                <option value="SESSION_CONFLICT">🚨 Conflits de session uniquement</option>
                <option value="LOGIN">🔑 Connexions réusies (LOGIN)</option>
                <option value="LOGIN_BLOCKED_INACTIVE">⛔ Logins Bloqués (Compte Inactif)</option>
              </select>

              {/* Search input */}
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Rechercher par Email, IP..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    padding: '8px 12px 8px 32px', borderRadius: '10px', fontSize: '12px',
                    border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-heading)', width: '220px'
                  }}
                />
              </div>
            </div>
          </div>

          {logs.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              Aucun log d'événement correspondant au filtre.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '12px' }}>Date & Heure</th>
                    <th style={{ padding: '12px' }}>Utilisateur</th>
                    <th style={{ padding: '12px' }}>Événement</th>
                    <th style={{ padding: '12px' }}>IP Client</th>
                    <th style={{ padding: '12px' }}>Détails de la Session</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: '12px' }}>
                        {formatDate(log.created_at)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-heading)' }}>
                          {log.user?.full_name || 'Inconnu'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#2563eb' }}>
                          {log.user?.email || `User #${log.user_id}`}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {log.action === 'SESSION_CONFLICT' && (
                          <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 800, background: '#fee2e2', color: '#991b1b' }}>
                            🚨 SESSION CONFLICT
                          </span>
                        )}
                        {log.action === 'LOGIN' && (
                          <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, background: '#dcfce7', color: '#166534' }}>
                            🔑 LOGIN OK
                          </span>
                        )}
                        {log.action !== 'SESSION_CONFLICT' && log.action !== 'LOGIN' && (
                          <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                            {log.action}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{log.ip_address || '—'}</span>
                        {log.ip_location && (
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px' }}>
                            ({log.ip_location})
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px', color: 'var(--text-secondary)', maxWidth: '450px', lineHeight: 1.5 }}>
                        {log.details || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Box>
      </div>
    </SiteLayout>
  );
};

export default AdminSessionAnomaliesScreen;
